import Store from './Store';

const SENTRY_LOG_TRACE_KEY = 'sentryLogTraceId';
const SENTRY_LOG_SOURCE = 'mwx.log';
const SENTRY_LOG_LEVEL_MAP = {
  log: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error'
};
const SENTRY_LOG_REPORT_TYPE_MAP = {
  warn: true,
  error: true
};
let currentOrderNo = '';

/**
 * 清理 Sentry 字符串字段，过滤历史链路中可能出现的脏空值。
 * @param {*} value - 原始字段值。
 * @returns {string} 可用于 Sentry 的字符串。
 */
function normalizeSentryText(value) {
  // 空值不进入 Sentry，避免生成不可检索的无效字段。
  if (value === undefined || value === null) {
    return '';
  }
  const text = String(value).trim();
  // 历史 URL 或缓存可能把空值序列化成字符串，统一视为空。
  if (!text || text === 'undefined' || text === 'null') {
    return '';
  }
  return text;
}

/**
 * 从候选值中读取第一个有效 Sentry 字符串。
 * @param {Array<*>} values - 候选字段值。
 * @returns {string} 第一个有效字符串。
 */
function getFirstSentryText(values) {
  for (let index = 0; index < values.length; index++) {
    const text = normalizeSentryText(values[index]);
    // 找到有效字段时立即返回，保持原有优先级。
    if (text) {
      return text;
    }
  }
  return '';
}

/**
 * 安全读取 sessionStorage 中的小程序数据。
 * @param {string} key - sessionStorage 缓存 key。
 * @returns {Object|null} 小程序数据。
 */
function getSessionAppData(key) {
  try {
    return Store.getSession(key) || null;
  } catch (error) {
    return null;
  }
}

/**
 * 获取当前 URL 中的查询参数片段。
 * @returns {string[]} 查询参数片段列表。
 */
function getCurrentUrlQueryStringList() {
  const search = ((window.location && window.location.search) || '').replace(
    /^\?/,
    ''
  );
  const hash = (window.location && window.location.hash) || '';
  const hashQueryIndex = hash.indexOf('?');
  const hashQuery = hashQueryIndex > -1 ? hash.slice(hashQueryIndex + 1) : '';
  return [search, hashQuery].filter(Boolean);
}

/**
 * 从指定查询参数片段中读取第一个有效参数值。
 * @param {string} queryString - 查询参数片段。
 * @param {string[]} names - 候选参数名。
 * @returns {string} 参数值。
 */
function getQueryValueFromString(queryString, names) {
  try {
    const params = new URLSearchParams(queryString);
    return names.map((name) => params.get(name)).filter(Boolean)[0] || '';
  } catch (error) {
    return '';
  }
}

/**
 * 从当前 URL 中读取第一个有效参数值，兼容 hash 路由参数。
 * @param {string[]} names - 候选参数名。
 * @returns {string} 参数值。
 */
function getCurrentUrlQueryValue(names) {
  return (
    getCurrentUrlQueryStringList()
      .map((queryString) => getQueryValueFromString(queryString, names))
      .filter(Boolean)[0] || ''
  );
}

/**
 * 生成本地日志批次与 Sentry Logs 互查用的标识。
 * @returns {string} 日志批次标识。
 */
function createSentryLogTraceId() {
  return `log-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * 获取当前日志批次的互查标识。
 * @param {string|number} [logTraceId] - 外部指定的互查标识。
 * @returns {string} 日志批次标识。
 */
export function getSentryLogTraceId(logTraceId) {
  const currentLogTraceId =
    logTraceId || Store.getSession(SENTRY_LOG_TRACE_KEY) || '';
  // 当前批次没有互查标识时生成一个新标识。
  if (!currentLogTraceId) {
    const nextLogTraceId = createSentryLogTraceId();
    Store.setSession(SENTRY_LOG_TRACE_KEY, nextLogTraceId);
    return nextLogTraceId;
  }
  return String(currentLogTraceId);
}

/**
 * 清理当前日志批次的互查标识。
 * @returns {void}
 */
export function resetSentryLogTraceId() {
  Store.removeSession(SENTRY_LOG_TRACE_KEY);
}

/**
 * 获取当前可用于 Sentry 的稳定用户标识。
 * @param {Object} [options] - 外部传入的用户标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @returns {string} Sentry user.id。
 */
function resolveSentryUserId(options = {}) {
  return getFirstSentryText([
    options.userId,
    Store.get('__uid') || '',
    options.deviceId,
    Store.get('deviceId'),
    options.uid,
    Store.get('uid')
  ]);
}

/**
 * 获取当前可用于 Sentry 的系统订单号。
 * @param {Object} [options] - 外部传入的订单信息。
 * @param {string|number} [options.soId] - 系统订单号。
 * @returns {string} 系统订单号。
 */
function resolveSentrySoId(options = {}) {
  return getFirstSentryText([
    options.soId,
    Store.getSession('soId'),
    getCurrentUrlQueryValue(['soId', 'oid'])
  ]);
}

/**
 * 获取当前可用于 Sentry 的订单号。
 * @param {string|number} [orderNo] - 外部传入的订单号。
 * @returns {string} 订单号。
 */
function resolveSentryOrderNo(orderNo) {
  const wxData = window._wxData || getSessionAppData('wxData') || {};
  const dyData = window._dyData || getSessionAppData('dyData') || {};
  const soId = resolveSentrySoId();
  return getFirstSentryText([
    orderNo,
    window.__orderNo__,
    wxData.orderNo,
    dyData.orderNo,
    soId,
    getCurrentUrlQueryValue(['orderNo', 'order_no']),
    currentOrderNo
  ]);
}

/**
 * 设置 Sentry 用户标识，仅上报稳定 ID，不上传昵称、手机号等隐私信息。
 * @param {Object} [options] - 外部传入的用户标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @returns {void}
 */
export function setSentryUser(options = {}) {
  const id = resolveSentryUserId(options);
  // Sentry 未初始化或用户标识为空时不设置，避免产生无效用户维度。
  if (!window._Sentry || !id) {
    return;
  }
  window._Sentry.setUser({ id });
}

/**
 * 设置 Sentry 订单上下文，只上报订单号用于搜索和定位。
 * @param {string|number} orderNo - 订单号。
 * @returns {void}
 */
export function setSentryOrderNo(orderNo) {
  currentOrderNo = resolveSentryOrderNo(orderNo);
  // Sentry 未初始化或订单号为空时不设置订单维度。
  if (!window._Sentry || !currentOrderNo) {
    return;
  }
  // 新版 SDK 支持直接设置 tag/context 时优先使用新版 interface。
  if (
    typeof window._Sentry.setTag === 'function' &&
    typeof window._Sentry.setContext === 'function'
  ) {
    window._Sentry.setTag('order_no', currentOrderNo);
    window._Sentry.setContext('order', {
      orderNo: currentOrderNo
    });
    return;
  }
  // 旧版 SDK 仅提供 configureScope 时使用兼容写法。
  if (typeof window._Sentry.configureScope !== 'function') {
    return;
  }
  window._Sentry.configureScope((scope) => {
    scope.setTag('order_no', currentOrderNo);
    scope.setContext('order', {
      orderNo: currentOrderNo
    });
  });
}

/**
 * 获取当前页面路径信息。
 * @param {Object} [options] - 外部传入的页面信息。
 * @param {string} [options.pageHash] - 页面 hash。
 * @returns {string} 页面 hash。
 */
function resolveSentryPageHash(options = {}) {
  return String(
    options.pageHash || (window.location && window.location.hash) || ''
  ).replace(/([?&])t=[^&]*/g, '$1t=');
}

/**
 * 汇总 Sentry Logs 与现有日志系统共用的上下文。
 * @param {Object} [options] - 外部传入的上下文。
 * @param {string|number} [options.logTraceId] - 日志批次互查标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @param {string|number} [options.orderNo] - 订单号。
 * @param {string|number} [options.soId] - 系统订单号。
 * @param {boolean} [options.disableOrderNoFallback] - 是否禁用订单号兜底。
 * @param {string} [options.pageHash] - 页面 hash。
 * @returns {Object} 日志上下文。
 */
export function resolveSentryLogContext(options = {}) {
  const soId = resolveSentrySoId(options);
  const orderNo = options.disableOrderNoFallback
    ? normalizeSentryText(options.orderNo)
    : resolveSentryOrderNo(options.orderNo || soId);
  return {
    logTraceId: getSentryLogTraceId(options.logTraceId),
    userId: resolveSentryUserId(options),
    orderNo,
    soId,
    pageHash: resolveSentryPageHash(options)
  };
}

/**
 * 过滤 Sentry Logs 支持的属性值。
 * @param {Object} attributes - 原始属性。
 * @returns {Object} 可上报属性。
 */
function buildSentryLogAttributes(attributes) {
  return Object.keys(attributes).reduce((result, key) => {
    const value = attributes[key];
    // 字符串字段先清洗，避免 "undefined" 这类脏值进入 Logs 查询维度。
    if (typeof value === 'string') {
      const text = normalizeSentryText(value);
      // 清洗后为空的字符串不写入 attributes。
      if (text) {
        result[key] = text;
      }
      return result;
    }
    const isValidValue =
      typeof value === 'number' || typeof value === 'boolean';
    // Sentry Logs 只支持 string、number、boolean，空字符串不写入。
    if (isValidValue) {
      result[key] = value;
    }
    return result;
  }, {});
}

/**
 * 判断 Sentry Log 是否需要在上报前过滤。
 * @param {Object} log - 待上报日志。
 * @param {string} log.message - 日志内容。
 * @param {Object} log.attributes - 日志属性。
 * @returns {boolean} 是否过滤当前日志。
 */
function shouldFilterSentryLog(log) {
  const message = normalizeSentryText(log.message);
  const hasTrustedEventField =
    /"isTrusted"\s*:/.test(message) || /(^|[,{]\s*)isTrusted\s*:/.test(message);
  // 浏览器事件快照包含 isTrusted 字段时暂不进入 Sentry Logs。
  if (hasTrustedEventField) {
    return true;
  }
  // 后续过滤规则统一收敛在此方法内，当前先拦截结构化属性里的 isTrusted。
  if (
    log.attributes &&
    Object.prototype.hasOwnProperty.call(log.attributes, 'isTrusted')
  ) {
    return true;
  }
  return false;
}

/**
 * 生成写入现有日志文件头部的互查信息。
 * @param {Object} [options] - 外部传入的上下文。
 * @param {string|number} [options.logTraceId] - 日志批次互查标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.orderNo] - 订单号。
 * @param {string|number} [options.soId] - 系统订单号。
 * @param {string} [options.pageHash] - 页面 hash。
 * @returns {string} 日志文件头部内容。
 */
export function createSentryLogFileHeader(options = {}) {
  const context = resolveSentryLogContext(options);
  const attributes = buildSentryLogAttributes({
    log_trace_id: context.logTraceId,
    order_no: context.orderNo,
    so_id: context.soId,
    user_id: context.userId,
    page_hash: context.pageHash
  });
  const content = Object.keys(attributes)
    .map((key) => `${key}=${attributes[key]}`)
    .join(' ');
  return `[sentry] ${content}`;
}

/**
 * 上报本地 mwx.log 对应的 Sentry Log。
 * @param {string} type - 本地日志等级。
 * @param {string} message - 日志内容。
 * @param {Object} [options] - 上报选项。
 * @param {boolean} [options.force] - 是否跳过默认等级过滤。
 * @param {string|number} [options.logTraceId] - 日志批次互查标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @param {string|number} [options.orderNo] - 订单号。
 * @param {string|number} [options.soId] - 系统订单号。
 * @param {boolean} [options.disableOrderNoFallback] - 是否禁用订单号兜底。
 * @param {string} [options.pageHash] - 页面 hash。
 * @param {string} [options.source] - 日志来源。
 * @param {string} [options.runtimeScene] - 运行场景。
 * @param {string} [options.entryChannel] - 入口渠道。
 * @param {string|number} [options.gcd] - 谷子 ID。
 * @param {string|number} [options.legacyLogMediaId] - 现有日志系统文件 mediaId。
 * @param {number} [options.warning] - 现有日志系统 warning 标识。
 * @returns {void}
 */
export function reportSentryLog(type, message, options = {}) {
  try {
    const level = SENTRY_LOG_LEVEL_MAP[type] || 'info';
    // 默认只上报 warn/error，避免普通日志造成 Sentry 噪音。
    if (!options.force && !SENTRY_LOG_REPORT_TYPE_MAP[type]) {
      return;
    }
    // Sentry 未初始化或当前 SDK 不支持 Logs 时，不影响本地日志链路。
    if (
      !window._Sentry ||
      !window._Sentry.logger ||
      !window._Sentry.logger[level]
    ) {
      return;
    }
    const context = resolveSentryLogContext(options);
    setSentryUser({ userId: context.userId });
    // 存在有效订单号时才写入全局订单上下文，避免空值触发历史兜底。
    if (context.orderNo) {
      setSentryOrderNo(context.orderNo);
    }
    const attributes = buildSentryLogAttributes({
      source: options.source || SENTRY_LOG_SOURCE,
      log_type: type,
      log_trace_id: context.logTraceId,
      order_no: context.orderNo,
      so_id: context.soId,
      user_id: context.userId,
      page_hash: context.pageHash,
      runtime_scene: options.runtimeScene,
      entry_channel: options.entryChannel,
      gcd: options.gcd,
      legacy_log_media_id: options.legacyLogMediaId,
      warning: options.warning
    });
    // 命中过滤规则的日志只保留本地日志文件，不写入 Sentry Logs。
    if (shouldFilterSentryLog({ message, attributes })) {
      return;
    }
    window._Sentry.logger[level](String(message || ''), attributes);
  } catch (error) {}
}

/**
 * 上报进入 H5 制作链路的渠道日志，仅在存在有效订单号或谷子 ID 时写入 Sentry Logs。
 * @param {string} runtimeScene - 运行场景。
 * @param {Object} [options] - 上报选项。
 * @param {string|number} [options.orderNo] - 订单号。
 * @param {string|number} [options.soId] - 系统订单号。
 * @param {string|number} [options.gcd] - 谷子 ID。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @param {string} [options.pageHash] - 页面 hash。
 * @returns {void}
 */
export function reportSentryEntryLog(runtimeScene, options = {}) {
  const entryOrderNo = normalizeSentryText(options.orderNo);
  const gcd = normalizeSentryText(options.gcd);
  // 没有有效订单号或谷子 ID 时不上报入口日志，避免 Sentry Logs 出现无业务标识记录。
  if (!entryOrderNo && !gcd) {
    return;
  }
  const context = resolveSentryLogContext({
    ...options,
    orderNo: entryOrderNo,
    disableOrderNoFallback: true
  });
  reportSentryLog('info', 'entry channel opened', {
    ...options,
    force: true,
    source: 'mwx.entry',
    runtimeScene,
    entryChannel: runtimeScene,
    orderNo: entryOrderNo,
    disableOrderNoFallback: true,
    soId: context.soId,
    userId: context.userId,
    gcd
  });
}

/**
 * 上报现有日志系统文件上传成功的关联日志。
 * @param {string|number} mediaId - 现有日志系统文件 mediaId。
 * @param {Object} [options] - 上报选项。
 * @param {string|number} [options.logTraceId] - 日志批次互查标识。
 * @param {string|number} [options.orderNo] - 订单号。
 * @param {string|number} [options.soId] - 系统订单号。
 * @param {number} [options.warning] - 现有日志系统 warning 标识。
 * @returns {void}
 */
export function reportSentryLegacyLogUpload(mediaId, options = {}) {
  // reportSentryLog('info', 'legacy log uploaded', {
  //   ...options,
  //   legacyLogMediaId: mediaId,
  //   force: true
  // });
}

/**
 * 初始化 Sentry 全局上下文，补齐用户标识和页面已有订单号。
 * @returns {void}
 */
export function initSentryScope() {
  setSentryUser();
  setSentryOrderNo();
}

/**
 * 手动上报已 catch 的业务错误，确保流程继续时仍能在 Sentry 中定位。
 * @param {Error} error - 需要上报的错误对象。
 * @param {Object} [options] - 上报选项。
 * @param {string} [options.name] - 业务错误名称。
 * @param {string} [options.level] - Sentry 错误等级。
 * @param {Object} [options.context] - 额外业务上下文。
 * @param {string|number} [options.orderNo] - 本次错误关联的订单号。
 * @returns {void}
 */
export function captureBusinessError(error, options = {}) {
  const context = resolveSentryLogContext(options);
  setSentryUser({ userId: context.userId });
  // 只上报 Error 对象，字符串日志继续留在本地 logger，避免制造 Sentry 噪音。
  if (!window._Sentry || !(error instanceof Error)) {
    return;
  }
  window._Sentry.withScope((scope) => {
    scope.setTag('business_error', 'true');
    scope.setTag('handled', 'true');
    scope.setTag('log_trace_id', context.logTraceId);
    scope.setLevel(options.level || 'error');
    // 有订单号时同时写入 tag 和 context，兼顾搜索与详情查看。
    if (context.orderNo) {
      scope.setTag('order_no', context.orderNo);
      scope.setContext('order', {
        orderNo: context.orderNo,
        soId: context.soId
      });
    }
    // 有业务错误名称时写入 tag，方便按类型聚合筛选。
    if (options.name) {
      scope.setTag('business_error_name', options.name);
    }
    // 有额外上下文时写入 business context，避免污染顶层字段。
    if (options.context) {
      scope.setContext('business', options.context);
    }
    window._Sentry.captureException(error);
  });
}
